import { StyleSheet, Platform } from 'react-native';
import type { Theme } from '../../../src/theme/getTheme';

export interface LeaderboardSelfStandingBarStyleOptions {
  /** Bottom safe-area inset so the bar clears the iOS home indicator. */
  bottomInset: number;
  /** Window width — used to match the leaderboard's centered web column. */
  windowWidth: number;
}

/**
 * "Your standing" bar — an always-visible, tappable summary pinned to the bottom
 * of a leaderboard (squad page and global leaderboard) so a member never has to
 * scroll a long roster to find their own rank/points. Visual language mirrors the
 * row `cardOuterMe` "me" highlight (primary left accent + tint + glow) but on an
 * elevated, opaque surface so it reads as a floating layer above the list.
 */
export const getLeaderboardSelfStandingBarStyles = (
  theme: Theme,
  { bottomInset, windowWidth }: LeaderboardSelfStandingBarStyleOptions,
) => {
  const webMaxWidth =
    Platform.OS === 'web'
      ? windowWidth >= 1024
        ? 880
        : windowWidth >= 768
          ? 700
          : 560
      : undefined;

  return StyleSheet.create({
    /**
     * Full-width positioned layer. `box-none` lets scroll gestures pass through
     * the transparent gutters on either side of the centered bar (web column).
     */
    wrap: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      alignItems: 'center',
    },
    inner: {
      width: '100%',
      ...(webMaxWidth != null && { maxWidth: webMaxWidth }),
      paddingHorizontal: 12,
      paddingBottom: bottomInset + 10,
    },
    pressRoot: {
      borderRadius: 16,
      overflow: 'hidden',
    },
    bar: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      paddingVertical: 9,
      paddingHorizontal: 12,
      borderRadius: 16,
      borderWidth: 1,
      borderColor: theme.primary + '80',
      borderLeftWidth: 3,
      borderLeftColor: theme.primary,
      backgroundColor: theme.background.cardElevated,
      ...(Platform.OS === 'web' && {
        backdropFilter: 'saturate(150%) blur(12px)' as never,
        WebkitBackdropFilter: 'saturate(150%) blur(12px)' as never,
        boxShadow:
          `0 -2px 18px ${theme.black}33, 0 0 16px 1px ${theme.primary}33, inset 0 1px 0 ${theme.white}14` as never,
      }),
      ...(Platform.OS !== 'web' && {
        shadowColor: theme.primary,
        shadowOffset: { width: 0, height: -2 },
        shadowOpacity: 0.3,
        shadowRadius: 12,
        elevation: 10,
      }),
    },
    barPressed: {
      opacity: 0.94,
    },
    barHover: {
      ...(Platform.OS === 'web' && {
        borderColor: theme.primary,
      }),
    },
    // Row-like columns — mirror LeaderboardRow metrics so the bar reads as a row,
    // while the surface treatment above keeps it visibly "you", not table content.
    rank: {
      minWidth: 26,
      textAlign: 'center',
      fontSize: 13,
      fontWeight: '800',
      color: theme.primary,
      flexShrink: 0,
      fontVariant: ['tabular-nums'],
    },
    youChip: {
      paddingHorizontal: 6,
      paddingVertical: 1,
      borderRadius: 999,
      backgroundColor: theme.primary,
      flexShrink: 0,
    },
    youChipText: {
      fontSize: 9,
      fontWeight: '900',
      letterSpacing: 0.6,
      color: theme.onPrimary,
    },
    name: {
      flex: 1,
      minWidth: 0,
      fontSize: 13,
      fontWeight: '700',
      color: theme.primary,
    },
    points: {
      fontSize: 13,
      fontWeight: '800',
      color: theme.gray[900],
      textAlign: 'right',
      flexShrink: 0,
      fontVariant: ['tabular-nums'],
    },
  });
};
